import {
  Badge,
  Banner,
  Button,
  Card,
  CardContent,
  Tabs,
  buttonVariants,
  cn,
} from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState, type ReactNode } from "react";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { listPlanInstances, recurrenceLabel } from "../plan/plan-api.js";
import {
  assignChecks,
  listPlanChecks,
  type PlanCheck,
} from "./checklist-api.js";
import { listStaff, type StaffProfile } from "./setup-api.js";
import {
  assignmentChanges,
  toggleId,
  unassignedChecks,
} from "./setup-checklist-model.js";

type Filter = "all" | "unassigned";

function Checkbox({
  checked,
  children,
  onChange,
}: {
  checked: boolean;
  children: ReactNode;
  onChange: () => void;
}) {
  return (
    <label
      className={cn(
        "flex min-h-12 cursor-pointer items-center gap-3 px-4 py-2.5 transition hover:bg-stone-50",
        checked && "bg-emerald-50/60",
      )}
    >
      <input
        checked={checked}
        className="size-4 accent-emerald-800"
        onChange={onChange}
        type="checkbox"
      />
      {children}
    </label>
  );
}

export function AssignChecks({ locationId }: { locationId: string }) {
  const queryClient = useQueryClient();
  const plans = useQuery({
    queryFn: () => listPlanInstances(locationId),
    queryKey: ["plan-instances", locationId],
  });
  const published =
    plans.data?.some((instance) => instance.state === "published") ?? false;
  const checks = useQuery({
    queryFn: () => listPlanChecks(locationId),
    queryKey: ["plan-checks", locationId],
    refetchInterval: (query) =>
      published && query.state.data?.length === 0 ? 5_000 : false,
  });
  const staff = useQuery({
    queryFn: () => listStaff(locationId),
    queryKey: ["staff", locationId],
  });
  const [filter, setFilter] = useState<Filter | null>(null);
  const [selectedChecks, setSelectedChecks] = useState<string[] | null>(null);
  const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
  const [outcome, setOutcome] = useState<{
    failed: number;
    saved: number;
  } | null>(null);

  const people = useMemo(
    () => (staff.data ?? []).filter((person) => person.status === "active"),
    [staff.data],
  );
  const names = useMemo(
    () => new Map(people.map((person) => [person.id, person.display_name])),
    [people],
  );
  const everyoneSelected =
    people.length > 0 &&
    people.every((person) => selectedPeople.includes(person.id));
  const all = checks.data ?? [];
  const unassigned = unassignedChecks(all);
  const activeFilter: Filter =
    filter ?? (unassigned.length > 0 ? "unassigned" : "all");
  const shown = activeFilter === "unassigned" ? unassigned : all;
  // Until someone picks, the checks nobody is assigned to are chosen.
  const chosen = selectedChecks ?? unassigned.map((check) => check.id);

  const assign = useMutation({
    mutationFn: () =>
      assignChecks(assignmentChanges(chosen, selectedPeople, everyoneSelected)),
    onSuccess: async (result) => {
      setOutcome(result);
      setSelectedChecks(result.failedIds);
      await queryClient.invalidateQueries({
        queryKey: ["plan-checks", locationId],
      });
      await queryClient.invalidateQueries({ queryKey: ["task-definitions"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  if (plans.isPending || checks.isPending || staff.isPending) {
    return <LoadingState label="Loading checks" />;
  }
  const failure = plans.error ?? checks.error ?? staff.error;
  if (failure) {
    return (
      <ErrorState
        message={displayError(failure)}
        onAction={() => {
          void plans.refetch();
          void checks.refetch();
          void staff.refetch();
        }}
        title="Couldn’t load checks"
      />
    );
  }
  if (!published) {
    return (
      <EmptyState
        action={
          <Link
            className={buttonVariants()}
            search={{ section: "plan" }}
            to="/setup"
          >
            Answer the plan questions
          </Link>
        }
        title="No checks yet"
      />
    );
  }
  if (all.length === 0) return <EmptyState title="No checks yet" />;
  if (people.length === 0) {
    return (
      <EmptyState
        action={
          <Link className={buttonVariants()} to="/team">
            Add a person
          </Link>
        }
        title="Add your first person"
      />
    );
  }

  const shownIds = shown.map((check) => check.id);
  const allShownSelected =
    shownIds.length > 0 && shownIds.every((id) => chosen.includes(id));

  function assigneeNames(check: PlanCheck): string {
    if (check.assign_everyone) return "Everyone";
    return check.assignee_staff_profile_ids
      .map((id) => names.get(id))
      .filter((name): name is string => Boolean(name))
      .join(", ");
  }

  return (
    <div className="space-y-5">
      {outcome && outcome.failed === 0 ? (
        <Banner
          title={`${outcome.saved} ${outcome.saved === 1 ? "check" : "checks"} assigned`}
          tone="positive"
        />
      ) : null}
      {outcome && outcome.failed > 0 ? (
        <Banner
          title={`${outcome.failed} ${outcome.failed === 1 ? "check" : "checks"} not saved. Try again.`}
          tone="danger"
        />
      ) : null}
      {assign.error ? (
        <Banner title={displayError(assign.error)} tone="danger" />
      ) : null}

      <div className="grid items-start gap-5 lg:grid-cols-2">
        <Card>
          <CardContent className="p-0">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-stone-950">People</h3>
              <Button
                onClick={() =>
                  setSelectedPeople(
                    everyoneSelected ? [] : people.map((person) => person.id),
                  )
                }
                size="sm"
                variant="ghost"
              >
                {everyoneSelected ? "Clear" : "Everyone"}
              </Button>
            </div>
            <div className="divide-y divide-stone-100">
              {people.map((person: StaffProfile) => (
                <Checkbox
                  checked={selectedPeople.includes(person.id)}
                  key={person.id}
                  onChange={() =>
                    setSelectedPeople((current) => toggleId(current, person.id))
                  }
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">
                      {person.display_name}
                    </span>
                    {person.job_title ? (
                      <span className="block truncate text-xs text-stone-500">
                        {person.job_title}
                      </span>
                    ) : null}
                  </span>
                  {!person.email && !person.user_id ? (
                    <Badge tone="attention">No sign-in</Badge>
                  ) : null}
                </Checkbox>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 px-4 py-3">
              <Tabs
                ariaLabel="Checks"
                items={[
                  {
                    badge:
                      unassigned.length > 0 ? (
                        <Badge tone="attention">{unassigned.length}</Badge>
                      ) : undefined,
                    label: "Nobody assigned",
                    value: "unassigned",
                  },
                  { label: "All checks", value: "all" },
                ]}
                onChange={(value) => setFilter(value as Filter)}
                value={activeFilter}
                variant="segmented"
              />
              {shown.length > 0 ? (
                <Button
                  onClick={() =>
                    setSelectedChecks(
                      allShownSelected
                        ? chosen.filter((id) => !shownIds.includes(id))
                        : [...new Set([...chosen, ...shownIds])],
                    )
                  }
                  size="sm"
                  variant="ghost"
                >
                  {allShownSelected ? "Clear" : "Select all"}
                </Button>
              ) : null}
            </div>
            {shown.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm font-semibold text-stone-600">
                All checks assigned
              </p>
            ) : (
              <div className="divide-y divide-stone-100">
                {shown.map((check) => (
                  <Checkbox
                    checked={chosen.includes(check.id)}
                    key={check.id}
                    onChange={() =>
                      setSelectedChecks(toggleId(chosen, check.id))
                    }
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">
                        {check.title}
                      </span>
                      <span className="block truncate text-xs text-stone-500">
                        {recurrenceLabel(check.schedule?.rrule)}
                      </span>
                    </span>
                    {check.assignee_staff_profile_ids.length === 0 ? (
                      activeFilter === "all" ? (
                        <Badge tone="attention">Nobody assigned</Badge>
                      ) : null
                    ) : (
                      <span className="max-w-40 truncate text-xs text-stone-500">
                        {assigneeNames(check)}
                      </span>
                    )}
                  </Checkbox>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3">
        <span className="text-sm font-semibold text-stone-600">
          {chosen.length} {chosen.length === 1 ? "check" : "checks"} ·{" "}
          {selectedPeople.length}{" "}
          {selectedPeople.length === 1 ? "person" : "people"}
        </span>
        <Button
          disabled={
            assign.isPending ||
            chosen.length === 0 ||
            selectedPeople.length === 0
          }
          onClick={() => {
            setOutcome(null);
            assign.mutate();
          }}
        >
          {assign.isPending ? "Assigning…" : "Assign"}
        </Button>
      </div>
    </div>
  );
}
